import React, { useMemo } from "react";
import * as THREE from "three";
import { useSelector, useDispatch } from "react-redux";
import {
  updatePosition,
  setMaxTimeVelMoreThan12,
} from "../../redux/infoWindRisk";
import { useState, useEffect } from "react";
import {
  setFirstWindIsShowing,
  setWindRiskIsLoading,
} from "../../redux/shadingSystem";
import { f, getPolygonCentroid, getRangeModeDisplayColor } from "../utils";
import { useColorBar } from "../CustomHooks";
import { reduxLocalStorage } from "../utility/storage";
import { parseJsonField } from "../utility/parseJson";
import { fetchWindRisk } from "../../api/windRiskApi";
import { VerticalWindowValueLabels } from "./VerticalWindowValueLabels";
import { HEATMAP_LABEL_LAYER } from "./heatmapLabelLayers";

const getPolygonPositions = (polygon) => {
  const parsed = parseJsonField(polygon, null);
  if (
    !Array.isArray(parsed) ||
    typeof parsed[0] !== "number" ||
    parsed.length < 9
  ) {
    return null;
  }
  return new Float32Array(parsed);
};

const getWindowPolygon = (windowItem) => {
  const parsed = parseJsonField(windowItem?.polygon, null);
  if (
    !Array.isArray(parsed) ||
    typeof parsed[0] !== "number" ||
    parsed.length < 9
  ) {
    return null;
  }
  return parsed;
};

const getWindowLabelPosition = (windowItem) => {
  const polygon = getWindowPolygon(windowItem);
  return polygon ? getPolygonCentroid(polygon) : [0, 0, 0];
};

const WindRiskWindowMesh = ({ windowItem, min, max, rangeMode, isVisible }) => {
  const dispatch = useDispatch();
  const positions = useMemo(
    () => getPolygonPositions(windowItem?.polygon),
    [windowItem?.polygon],
  );

  const { visible: inRangeVisible, color } = useMemo(
    () =>
      getRangeModeDisplayColor(windowItem?.time_velocity_more_than_12, {
        min,
        max,
        rangeMode,
        colorScale: f,
      }),
    [windowItem?.time_velocity_more_than_12, min, max, rangeMode],
  );

  if (!positions || !inRangeVisible) return null;

  return (
    <mesh
      dispose={null}
      userData={{ excludeFromLabelOcclusion: true }}
      onClick={(event) => {
        if (!isVisible) return;
        dispatch(setFirstWindIsShowing());
        dispatch(
          updatePosition([
            event.pageX,
            event.pageY,
            parseJsonField(windowItem.avg, []),
            parseJsonField(windowItem.max, []),
            windowItem.time_velocity_more_than_12,
            windowItem.name,
            windowItem.x,
            windowItem.y,
            windowItem.z,
            windowItem.critical_wind_speed,
          ]),
        );
        event.stopPropagation();
      }}
    >
      <bufferGeometry>
        <bufferAttribute
          attach="attributes-position"
          array={positions}
          count={positions.length / 3}
          itemSize={3}
        />
      </bufferGeometry>
      <meshBasicMaterial
        attach="material"
        side={THREE.DoubleSide}
        color={color}
      />
    </mesh>
  );
};

const WindRiskData = () => {
  const project_id = reduxLocalStorage.getItem("project_id");
  const [resultArray, setResultArray] = useState([]);
  const [nodes, setNodes] = useState(false);
  const dispatch = useDispatch();
  const windRiskSelector = useSelector((state) => state.shadingSystem.value);

  const { max_time_velocity_more_than_12: maxTimeVelMoreThan12 } = useSelector(
    (state) => state.infoWindRisk,
  );

  const maxVal = maxTimeVelMoreThan12;
  const { max, min, rangeMode } = useColorBar(
    "Shading System",
    "Wind Risk",
    0,
    maxVal,
  );
  const isVisible = windRiskSelector.checkStatus[0];

  const labelItems = useMemo(
    () => resultArray.filter((windowItem) => getWindowPolygon(windowItem)),
    [resultArray],
  );

  useEffect(() => {
    if (!isVisible || nodes) return;

    let cancelled = false;
    dispatch(setWindRiskIsLoading(true));
    fetchWindRisk(project_id)
      .then((windows) => {
        if (cancelled) return;
        const list = Array.isArray(windows) ? windows : [];
        setResultArray(list);
        setNodes(true);
        const nextMax = list.reduce(
          (total, current) =>
            total < Number(current?.time_velocity_more_than_12)
              ? Number(current.time_velocity_more_than_12)
              : total,
          0,
        );
        dispatch(setMaxTimeVelMoreThan12(nextMax));
      })
      .catch(() => {
        if (cancelled) return;
        setResultArray([]);
        setNodes(true);
      })
      .finally(() => {
        if (!cancelled) dispatch(setWindRiskIsLoading(false));
      });

    return () => {
      cancelled = true;
    };
  }, [isVisible, nodes, project_id, dispatch]);

  if (!nodes) return null;

  return (
    <group rotation-x={Math.PI * -0.5} visible={isVisible}>
      {resultArray.map((windowItem, index) => (
        <WindRiskWindowMesh
          key={windowItem?.name || index}
          windowItem={windowItem}
          min={min}
          max={max}
          rangeMode={rangeMode}
          isVisible={isVisible}
        />
      ))}
      <VerticalWindowValueLabels
        visible={isVisible}
        layerKey={HEATMAP_LABEL_LAYER.WIND_RISK}
        items={labelItems}
        updateKey={`${min}-${max}-${rangeMode}`}
        normalOffset={0.01}
        getVisible={(windowItem) =>
          getRangeModeDisplayColor(windowItem?.time_velocity_more_than_12, {
            min,
            max,
            rangeMode,
            colorScale: f,
          }).visible
        }
        getPosition={getWindowLabelPosition}
        getValue={(windowItem) => windowItem?.time_velocity_more_than_12}
        getLabelPolygon={getWindowPolygon}
      />
    </group>
  );
};

export default React.memo(WindRiskData);
